import Pin from "./Pin";
import Form from "react-bootstrap/Form";
import TimeField from "../libs/timeField";
import { getArrivalTimeAsDayjs, getDepartureTimeAsDayjs, } from "../libs/routes-lib";
import dayjs from "../dayjs";
import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from "react";
import { useScroll } from "../libs/hooksLib";
import { Col, Flex, Popover, Row, Tooltip, Alert } from "antd";
import { Button } from "react-bootstrap";
import { useAppContext } from "../libs/contextLib";
import config from "../config";
import { noop } from "lodash/util";
import { dayjsToSecsSinceMidnight } from "../model/timeFilter";
import RouteNumber from "./RouteNumber";
import { find } from "lodash/collection";
import { differenceBy, uniq } from "lodash/array";
import { EyeOutlined, DownOutlined, UpOutlined, BellOutlined, ExportOutlined } from "@ant-design/icons";
import { ReactComponent as Down } from "../assets/icons/Down.svg";
import ShiftBatSelectRowEdit from "../features/ShiftBat/ShiftBatSelectRowEdit";
import { ReactComponent as Check } from "../assets/icons/Check.svg";
import { ReactComponent as Close } from "../assets/icons/Close.svg";
import { ReactComponent as Transfers } from '../assets/icons/Transfers.svg';
import { ReactComponent as Walking } from '../assets/icons/Walking.svg';
import { values } from "lodash/object";
import ChatButton from "./ChatButton";
import LoadMessage from './LoadMessage';
import { Warning } from "./Icons";
import { toTime, toHrsMinsSecs } from "../libs/formatLib";
import { findLinkedSchoolStops, validateBellWindow } from "../model/school";
import useAllTransfers from "../hooks/useAllTransfers";
import useAllRoutes from '../hooks/useAllRoutes';
import useAllStops from '../hooks/useAllStops';
import { isSetdownTransfer } from '../features/Transfers/Transfers';

// Bell Timeline Visualization Component
const BellTimeline = ({ validation, isAM }) => {
    const {
        arrivalTime,
        departTime,
        schoolStop,
        isValidForStartBell,
        isValidForEndBell,
        hasBellTimes
    } = validation;

    if (!hasBellTimes) return null;

    const isStartBell = isAM;
    const busTime = isStartBell ? arrivalTime : departTime;
    const isValid = isStartBell ? isValidForStartBell : isValidForEndBell;

    // Parse times to seconds for calculations
    const parseTimeToSecs = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60;
    };

    const busTimeSecs = parseTimeToSecs(busTime);

    let bellStartSecs, bellEndSecs, bellTimeSecs;
    if (isStartBell && schoolStop.startBell && schoolStop.startBellWindow) {
        bellTimeSecs = schoolStop.startBell;
        bellStartSecs = schoolStop.startBell - schoolStop.startBellWindow;
        bellEndSecs = schoolStop.startBell;
    } else if (!isStartBell && schoolStop.endBell && schoolStop.endBellWindow) {
        bellTimeSecs = schoolStop.endBell;
        bellStartSecs = schoolStop.endBell;
        bellEndSecs = schoolStop.endBell + schoolStop.endBellWindow;
    } else {
        return null;
    }

    // Create timeline range (extend beyond bell window to show out-of-range times)
    const padding = 900; // 15 minutes padding in seconds
    const timelineStart = Math.min(bellStartSecs, busTimeSecs) - padding;
    const timelineEnd = Math.max(bellEndSecs, busTimeSecs) + padding;
    const timelineRange = timelineEnd - timelineStart;

    // Calculate positions as percentages
    const bellStartPos = ((bellStartSecs - timelineStart) / timelineRange) * 100;
    const bellEndPos = ((bellEndSecs - timelineStart) / timelineRange) * 100;
    const bellTimePos = ((bellTimeSecs - timelineStart) / timelineRange) * 100;
    const busTimePos = ((busTimeSecs - timelineStart) / timelineRange) * 100;

    // Calculate distance if outside window
    let distanceText = '';
    if (!isValid) {
        const distanceSecs = isStartBell
            ? (busTimeSecs < bellStartSecs ? bellStartSecs - busTimeSecs : busTimeSecs - bellEndSecs)
            : (busTimeSecs < bellStartSecs ? bellStartSecs - busTimeSecs : busTimeSecs - bellEndSecs);
        distanceText = toHrsMinsSecs(distanceSecs);
    }

    return (
        <div style={{
            padding: '6px 10px 8px',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#495057'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '6px' }}>
                    <Pin size={20} type={'school'} />
                    <span>{validation.schoolStop.stopName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {validation.schoolStop?.stopId && (
                        <Button
                            className="icon-button"
                            title="Open in Points"
                            size="sm"
                            variant="light"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (validation.schoolStop?.stopId) {
                                    window.open(`/points/${validation.schoolStop.stopId}`, '_blank', 'noopener,noreferrer');
                                }
                            }}
                        >
                            <ExportOutlined />
                        </Button>
                    )}
                </div>
            </div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '6px',
                marginBottom: '24px',
                fontSize: '13px',
                fontWeight: '500'
            }}>
                <span>
                    {isStartBell ? 'Start bell arrival' : 'End bell departure'}
                </span>
                {/* Validation Message - Only show when outside bell window */}
                {!isValid && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#F5222D',
                    }}>
                        <Warning style={{ height: 11 }} />
                        <span>
                            {distanceText} {busTimeSecs < bellStartSecs ? 'before' : 'after'} bell window
                        </span>
                    </div>
                )}
            </div>

            {/* Timeline Container */}
            <div style={{
                position: 'relative',
                height: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                marginBottom: '24px'
            }}>
                {/* Bell Window Bar */}
                <div style={{
                    position: 'absolute',
                    left: `${bellStartPos}%`,
                    width: `${bellEndPos - bellStartPos}%`,
                    height: '100%',
                    backgroundColor: '#722ed1',
                    borderRadius: '8px',
                    opacity: 0.4
                }} />

                {/* Bell Time Icon */}
                <div style={{
                    position: 'absolute',
                    left: `${bellTimePos}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 15,
                    backgroundColor: '#ffffff',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    border: '2px solid #1890ff'
                }}>
                    <BellOutlined style={{
                        fontSize: '12px',
                        color: '#1890ff',
                        fontWeight: 'bold'
                    }} />
                </div>

                {/* Bus Time Marker */}
                <div style={{
                    position: 'absolute',
                    left: `${busTimePos}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '8px',
                    height: '8px',
                    backgroundColor: isValid ? '#531dab' : '#F5222D',
                    borderRadius: '50%',
                    border: '1px solid #ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    zIndex: 10
                }} />

                {/* Window Start Time Label */}
                <Tooltip title={toTime(bellStartSecs)} placement="bottom">
                    <div style={{
                        position: 'absolute',
                        left: `${bellStartPos}%`,
                        bottom: '-24px',
                        transform: 'translateX(-50%)',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#6c757d',
                        whiteSpace: 'nowrap',
                        background: '#fff'
                    }}>
                        {toTime(bellStartSecs)}
                    </div>
                </Tooltip>

                {/* Window End Time Label */}
                <Tooltip title={toTime(bellEndSecs)} placement="bottom">
                    <div style={{
                        position: 'absolute',
                        left: `${bellEndPos}%`,
                        bottom: '-24px',
                        transform: 'translateX(-50%)',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#6c757d',
                        whiteSpace: 'nowrap',
                        background: '#fff'
                    }}>
                        {toTime(bellEndSecs)}
                    </div>
                </Tooltip>

                {/* Bus Time Label */}
                <div style={{
                    position: 'absolute',
                    left: `${busTimePos}%`,
                    top: '-24px',
                    transform: 'translateX(-50%)',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: isValid ? '#531dab' : '#F5222D',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    zIndex: 20
                }}>
                    {busTime}
                </div>
            </div>
        </div>
    );
};

function StopTimeField({
    stop,
    idx,
    validationMessages,
    showSeconds,
    setUpdated,
    trip,
    onChange,
    setEditing,
    arrive,
    value,
    focus, setFocus, cumulativeUpdate
}) {
    const timeFieldRef = useRef();

    useEffect(() => {
        if (timeFieldRef.current) {
            timeFieldRef.current.addEventListener('focus', () => {
                setFocus({ idx, arrive });
            })
            timeFieldRef.current.addEventListener('blur', () => {
                setFocus(null);
            })
        }
    }, [timeFieldRef, setFocus]);

    // const [format, setFormat] = useState("HH:mm")
    // const [ss, setSs] = useState(showSeconds)
    // useEffect(() => {
    //     if(showSeconds !== ss) {
    //         setFormat("HH:mm ")
    //     }
    // }, [showSeconds, ss, setFormat])
    const shouldFocus = () => {
        if (!focus) {
            return false
        }
        if (idx < focus.idx) {
            return false;
        }
        if (cumulativeUpdate) {
            return idx > focus.idx ? true : focus.idx === idx ? arrive === focus.arrive || focus.arrive : false;
        } else {
            return focus.idx === idx && focus.arrive === arrive;
        }
    }
    const getStyleForErrors = messages => !messages.length ? '' : messages.some(m => !m.warning) ? 'alert-danger' : 'alert-warning'
    return (
        <Tooltip
            title={<div>{validationMessages.map(m => (<p>{m.msg}</p>))}</div>}
            zIndex={!validationMessages?.length ? -10 : 10}>
            <TimeField
                inputRef={timeFieldRef}
                tabIndex={idx}
                className={`ant-input ant-input-sm inputtext-small text-center ${getStyleForErrors(validationMessages)} ${shouldFocus() ? 'alert-success' : ''}`}
                style={{ width: showSeconds ? 58 : 48 }}
                showSeconds={!!showSeconds}
                onFocus={() => setEditing(true)}
                onBlur={e => {
                    setEditing(false)
                    setUpdated(true)
                }}
                value={value}
                onChange={onChange}
            /></Tooltip>
    )
}

function NewStopTime({ allStops, setSelectedStop, setShowNewStop, buildToStop, charter }) {

    const [stop, setStop] = useState(null);
    const [options, setOptions] = useState([])

    useEffect(() => {
        // Set the service options whenallRoutes change
        setOptions(values(allStops).filter(stop => (!stop.duplicate || stop.duplicate <= 0) && (charter || !stop.stopType || stop.stopType === 'bus')).map(stop => ({
            label: stop.stopName + (stop.stopCode ? ` (${stop.stopCode})` : ''),
            value: stop.stopId
        })))
    }, [allStops, setOptions, charter])


    return (<>
        <div key={`StopTime-Row-New`} className={'mt-2 AddNewRow'}>
            <Row>
                <Col xs={20}>
                    <ShiftBatSelectRowEdit className={"inputselect-small w-100"} ttTitle={'Select a stop'}
                        suffixIcon={<Down />}
                        value={stop?.stopId || ""}
                        placeholder={"Select stop"}
                        onChange={value => {
                            const stop = allStops[value];
                            setStop(stop)
                            setSelectedStop(stop);
                        }}
                        options={options}
                    /></Col>
                <Col xs={4}>
                    <div className="sb-edit-controls">
                        <Button className="sb-done"
                            icon={<Check />}
                            disabled={!stop}
                            onClick={() => buildToStop(stop)} />
                        <Button className="sb-close"
                            icon={<Close />} onClick={() => setShowNewStop(false)} />
                    </div>
                </Col>
            </Row>
        </div>
    </>)

}

// Extracted TransferBadge component
const TransferBadge = memo(function TransferBadge({ transferValidation, schoolBellValidation, onClick, showTransfers, onMouseEnter, onMouseLeave, connectionCount }) {
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: (transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#fff2f0' : '#f0f8ff',
                border: `1px solid ${(transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#ffccc7' : '#bae7ff'}`,
                color: (transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#cf1322' : '#0958d9',
                transition: 'all 0.2s ease',
                minWidth: 'fit-content',
                maxWidth: '100%',
                flexShrink: 1,
                ...(schoolBellValidation && schoolBellValidation.length > 0 ? {
                    flex: '1 1 0',
                    minWidth: '120px'
                } : {})
            }}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Transfers style={{ height: 14, width: 14 }} />
            <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                textAlign: 'center'
            }}>
                {`${connectionCount} connection${connectionCount !== 1 ? 's' : ''}`}
            </span>
            {(transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) && <Warning style={{ height: 10, width: 10 }} />}
            {showTransfers ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
        </div>
    );
});

// Extracted SchoolBadge component
const SchoolBadge = memo(function SchoolBadge({ schoolBellValidation, showSchoolInfo, onClick, onMouseEnter, onMouseLeave }) {
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#fff2f0' : '#f0f8ff',
                border: `1px solid ${schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#ffccc7' : '#bae7ff'}`,
                color: schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#cf1322' : '#0958d9',
                transition: 'all 0.2s ease',
                minWidth: 'fit-content',
                maxWidth: '100%',
                flexShrink: 1,
                ...(schoolBellValidation.length > 0 ? {
                    flex: '1 1 0',
                    minWidth: '120px'
                } : {})
            }}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Pin size={14} type={'school'} />
            <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                textAlign: 'center'
            }}>
                {schoolBellValidation.length === 1
                    ? schoolBellValidation[0].schoolStop.stopName
                    : `${schoolBellValidation.length} schools`
                }
            </span>
            {schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) && <Warning style={{ height: 10, width: 10 }} />}
            {showSchoolInfo ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
        </div>
    );
});

// Helper for transfer time difference and display
function getTransferTimeDisplay(tx, stop, getTransferWithTiming, toHrsMinsSecs) {
    let timeDiff = null;
    let requiresWalking = false;
    const isTransferTo = tx.isTransferTo;
    const referenceTime = isTransferTo ? stop.arriveSecs : stop.departSecs;
    if (tx.departSecs !== undefined && tx.departSecs !== null) {
        timeDiff = tx.departSecs - referenceTime;
    } else if (tx.arriveSecs !== undefined && tx.arriveSecs !== null) {
        timeDiff = tx.arriveSecs - referenceTime;
    } else if (tx.departTime) {
        const [hours, minutes] = tx.departTime.split(':').map(Number);
        const transferSecs = hours * 3600 + minutes * 60;
        timeDiff = transferSecs - referenceTime;
    } else if (tx.arrivalTime) {
        const [hours, minutes] = tx.arrivalTime.split(':').map(Number);
        const transferSecs = hours * 3600 + minutes * 60;
        timeDiff = transferSecs - referenceTime;
    } else {
        const fullTransfer = getTransferWithTiming(tx.routeId, stop.stopId, !isTransferTo);
        if (fullTransfer) {
            if (fullTransfer.departSecs !== undefined && fullTransfer.departSecs !== null) {
                timeDiff = fullTransfer.departSecs - referenceTime;
            } else if (fullTransfer.arriveSecs !== undefined && fullTransfer.arriveSecs !== null) {
                timeDiff = fullTransfer.arriveSecs - referenceTime;
            }
        }
    }
    if (tx.fromStopId && tx.toStopId && tx.fromStopId !== tx.toStopId) {
        requiresWalking = true;
    }
    let prefix = '+';
    let timeDisplay = '0';
    if (timeDiff !== null && !isNaN(timeDiff)) {
        prefix = timeDiff > 0 ? '+' : (timeDiff < 0 ? '-' : '+');
        timeDisplay = timeDiff === 0 ? '0' : toHrsMinsSecs(Math.abs(timeDiff));
    }
    return { timeDiff, requiresWalking, prefix, timeDisplay };
}

// Extracted TransferDetailsCard component
const TransferDetailsCard = memo(function TransferDetailsCard({ uniqueTransfers, stop, allStops, focusTransfers, setFocusTransfers, getTransferWithTiming, toHrsMinsSecs, Walking, RouteNumber }) {
    return (
        <div className="transfer-details-card" style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '12px',
            margin: '8px 0',
            marginLeft: '20px',
            width: 'calc(100% - 20px)'
        }}>
            <div className="transfer-section">
                <div className="transfer-header" style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#495057'
                }}>
                    <EyeOutlined style={{ marginRight: '6px' }} />
                    <span>Connections ({uniqueTransfers.length})</span>
                </div>
                <div className="transfer-routes" style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px'
                }}>
                    {uniqueTransfers.map((tx, idx) => {
                        const { timeDiff, requiresWalking, prefix, timeDisplay } = getTransferTimeDisplay(tx, stop, getTransferWithTiming, toHrsMinsSecs);
                        return (
                            <div
                                key={'tx_' + tx.routeId}
                                className={`transfer-single ${tx.invalid ? "transfer-warning" : ""} ${(focusTransfers.to.filter(t => t.routeId === tx.routeId).length || focusTransfers.from.filter(t => t.routeId === tx.routeId).length) ? 'Selected' : ''}`}
                                onClick={e => {
                                    if (e.metaKey) return;
                                    e.stopPropagation();
                                    setFocusTransfers(focusTransfers => {
                                        const isInTo = focusTransfers.to.find(t => t.routeId === tx.routeId);
                                        const isInFrom = focusTransfers.from.find(t => t.routeId === tx.routeId);
                                        if (isInTo || isInFrom) {
                                            focusTransfers.to = focusTransfers.to.filter(t => t.routeId !== tx.routeId);
                                            focusTransfers.from = focusTransfers.from.filter(t => t.routeId !== tx.routeId);
                                        } else {
                                            if (stop.transfersTo?.some(t => t.routeId === tx.routeId)) {
                                                focusTransfers.to = focusTransfers.to.concat(tx);
                                            }
                                            if (stop.transfersFrom?.some(t => t.routeId === tx.routeId)) {
                                                focusTransfers.from = focusTransfers.from.concat(tx);
                                            }
                                        }
                                        return {
                                            ...focusTransfers,
                                            stop: focusTransfers.from?.length || focusTransfers.to?.length ? { ...stop, ...allStops[stop.stopId] } : null
                                        };
                                    });
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    {/* Direction indicator */}
                                    {(() => {
                                        const showTo = tx.isTransferTo;
                                        const showFrom = tx.isTransferFrom;
                                        const arrow = showTo && showFrom ? '↔' : showTo ? '→' : '←';
                                        const isTransferTo = showTo;
                                        return (
                                            <div style={{
                                                position: 'absolute',
                                                top: '-6px',
                                                left: '-6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                backgroundColor: isTransferTo ? '#e6f7ff' : '#f6ffed',
                                                border: `1px solid ${isTransferTo ? '#91d5ff' : '#b7eb8f'}`,
                                                zIndex: 15
                                            }}>
                                                <span style={{
                                                    fontSize: '8px',
                                                    fontWeight: '600',
                                                    color: isTransferTo ? '#1890ff' : '#52c41a'
                                                }}>
                                                    {arrow}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    {/* Timing badge */}
                                    {(timeDiff !== null && !isNaN(timeDiff)) && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px',
                                            zIndex: 10
                                        }}>
                                            {requiresWalking && (
                                                <Walking
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        fill: '#6c757d'
                                                    }}
                                                />
                                            )}
                                            <span style={{
                                                fontSize: '9px',
                                                color: (tx.isTransferTo && timeDiff >= 0) || (!tx.isTransferTo && timeDiff <= 0) ? '#007bff' : '#dc3545',
                                                fontWeight: '600',
                                                backgroundColor: '#f8f9fa',
                                                padding: '1px 4px',
                                                borderRadius: '6px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                {prefix}{timeDisplay}
                                            </span>
                                        </div>
                                    )}
                                    <RouteNumber
                                        key={'TxRid' + tx.routeId}
                                        route={tx}
                                        link={true}
                                        meta={true}
                                        tooltip={tx.invalid && tx.warnings?.length ? uniq(tx.warnings).map(w => <p key={w}>{w}</p>) : null}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

function StopTime({
    route, charter,
    idx,
    trip,
    stopTimes,
    stopValidationMessages,
    arriveValidationMessages,
    departValidationMessages,
    isAdmin,
    immutable,
    setEditing,
    editing,
    setFocusStop,
    setSelectedStop,
    selectedStop,
    scrollRef,
    setTrip,
    setUpdated,
    stop,
    allStops,
    operator,
    showSeconds,
    cumulativeUpdate,
    focus,
    setFocus,
    focusTransfers,
    setFocusTransfers = noop,
}) {

    const { setChatContext, setChatOpen } = useAppContext();
    const [showTransfers, setShowTransfers] = useState(false);
    const [showSchoolInfo, setShowSchoolInfo] = useState(false);
    const { allTransfers } = useAllTransfers();

    // Function to find full transfer data with timing information
    const getTransferWithTiming = useCallback((routeId, stopId, isFrom = false, fromRouteId) => {
        if (!allTransfers) return null;
        const transfers = Object.values(allTransfers);

        return transfers.find(transfer => {
            if (isFrom) {
                // For pickup transfers, broad match
                return (transfer.fromRouteId === routeId || transfer.toRouteId === routeId) &&
                    (transfer.fromStopId === stopId || transfer.toStopId === stopId) &&
                    (fromRouteId ? transfer.fromRouteId === fromRouteId || transfer.toRouteId === fromRouteId : true);
            } else {
                // For setdown transfers, try multiple patterns
                return (
                    (transfer.fromRouteId === routeId && transfer.fromStopId === stopId && transfer.toRouteId === routeId) ||
                    (transfer.fromRouteId === routeId && transfer.fromStopId === stopId) ||
                    (transfer.toRouteId === routeId && transfer.toStopId === stopId) ||
                    ((transfer.fromRouteId === routeId || transfer.toRouteId === routeId) &&
                    (transfer.fromStopId === stopId || transfer.toStopId === stopId))
                );
            }
        });
    }, [allTransfers]);

    // School bell time validation logic (using shared utilities from model/school.js)
    const getSchoolBellValidation = () => {
        const currentStop = allStops[stop.stopId];
        if (!currentStop) return null;

        // Find linked school stops using shared utility
        const linkedSchoolStops = findLinkedSchoolStops(currentStop.stopId, allStops);
        if (linkedSchoolStops.length === 0) return null;

        const arrivalTime = trip.getArrivalTimeAsDayjs(stop);
        const departTime = trip.getDepartureTimeAsDayjs(stop);
        const arrivalSecs = dayjsToSecsSinceMidnight(arrivalTime);
        const departSecs = dayjsToSecsSinceMidnight(departTime);

        const validationResults = linkedSchoolStops.map(schoolStop => {
            // Use shared validation utility
            const validation = validateBellWindow(arrivalSecs, departSecs, schoolStop);

            return {
                schoolStop,
                isValid: validation.isValid,
                isValidForStartBell: validation.isValidForStartBell,
                isValidForEndBell: validation.isValidForEndBell,
                hasBellTimes: validation.hasBellTimes,
                arrivalTime: arrivalTime.format('HH:mm'),
                departTime: departTime.format('HH:mm'),
                startBell: schoolStop.startBell ? toTime(schoolStop.startBell) : 'Not set',
                startBellWindow: schoolStop.startBellWindow ? `${toTime(schoolStop.startBell - schoolStop.startBellWindow)} - ${toTime(schoolStop.startBell)}` : 'Not set',
                endBell: schoolStop.endBell ? toTime(schoolStop.endBell) : 'Not set',
                endBellWindow: schoolStop.endBellWindow ? `${toTime(schoolStop.endBell)} - ${toTime(schoolStop.endBell + schoolStop.endBellWindow)}` : 'Not set',
                // New formatted bell windows with the bell time highlighted
                startBellWindowFormatted: schoolStop.startBellWindow ? {
                    startTime: toTime(schoolStop.startBell - schoolStop.startBellWindow),
                    bellTime: toTime(schoolStop.startBell),
                    endTime: toTime(schoolStop.startBell)
                } : null,
                endBellWindowFormatted: schoolStop.endBellWindow ? {
                    startTime: toTime(schoolStop.endBell),
                    bellTime: toTime(schoolStop.endBell),
                    endTime: toTime(schoolStop.endBell + schoolStop.endBellWindow)
                } : null
            };
        });

        return validationResults;
    };

    // Transfer validation logic
    const getTransferValidation = () => {
        if (!stop.transfersTo?.length && !stop.transfersFrom?.length) return null;

        const transferResults = {
            transfersTo: [],
            transfersFrom: [],
            hasWarnings: false
        };

        // Check transfers to (setdown/outbound connections)
        if (stop.transfersTo?.length > 0) {
            stop.transfersTo.forEach(transfer => {
                const transferInvalid = transfer.invalid || transfer.warnings?.length > 0;
                const warnings = [...(transfer.warnings || [])];

                // Check timing-based validation
                const fullTransfer = getTransferWithTiming(transfer.routeId, stop.stopId, false);
                if (fullTransfer && fullTransfer.departSecs != null && fullTransfer.arriveSecs != null) {
                    const currentServiceArrivalSecs = stop.arriveSecs;
                    const transferDepartSecs = fullTransfer.departSecs;
                    const timeDiff = transferDepartSecs - currentServiceArrivalSecs;

                    if (timeDiff < 0) {
                        warnings.push(`Transfer service departs ${toHrsMinsSecs(Math.abs(timeDiff))} before current service arrives - passengers will miss this connection`);
                    }
                }

                const hasTimingWarnings = warnings.length > (transfer.warnings?.length || 0);
                const isInvalid = transferInvalid || hasTimingWarnings;
                if (isInvalid) transferResults.hasWarnings = true;

                transferResults.transfersTo.push({
                    transfer,
                    invalid: isInvalid,
                    warnings: warnings,
                    hasTimingIssue: hasTimingWarnings,
                    stopTimeId: fullTransfer?.toStopTimeId,
                    tripId: fullTransfer?.toTripId,
                    stopId: fullTransfer?.toStopId
                });
            });
        }

        // Check transfers from (pickup/inbound connections)
        if (stop.transfersFrom?.length > 0) {
            stop.transfersFrom.forEach(transfer => {
                const transferInvalid = transfer.invalid || transfer.warnings?.length > 0;
                const warnings = [...(transfer.warnings || [])];

                // Check timing-based validation
                const fullTransfer = getTransferWithTiming(transfer.routeId, stop.stopId, true, route.routeId);
                if (fullTransfer && fullTransfer.departSecs != null && fullTransfer.arriveSecs != null) {
                    const currentServiceDepartSecs = stop.departSecs;
                    const pickupArrivalSecs = fullTransfer.arriveSecs;
                    const timeDiff = pickupArrivalSecs - currentServiceDepartSecs;

                    if (timeDiff > 0) {
                        warnings.push(`Pickup service arrives ${toHrsMinsSecs(Math.abs(timeDiff))} after current service departs - passengers will miss the current service`);
                    }
                }

                const hasTimingWarnings = warnings.length > (transfer.warnings?.length || 0);
                const isInvalid = transferInvalid || hasTimingWarnings;
                if (isInvalid) transferResults.hasWarnings = true;

                transferResults.transfersFrom.push({
                    transfer,
                    invalid: isInvalid,
                    warnings: warnings,
                    hasTimingIssue: hasTimingWarnings,
                    stopTimeId: fullTransfer?.fromStopTimeId,
                    tripId: fullTransfer?.fromTripId,
                    stopId: fullTransfer?.fromStopId
                });
            });
        }

        return transferResults;
    };

    // Memoize school bell validation, but only if School route type
    const isSchoolTrip = trip.getRouteType && trip.getRouteType() === 'School';
    const schoolBellValidation = useMemo(() => isSchoolTrip ? getSchoolBellValidation() : [], [isSchoolTrip, allStops, stop.stopId, trip, trip.tripId]);
    // Memoize transfer validation
    const transferValidation = useMemo(() => getTransferValidation(), [stop.transfersTo, stop.transfersFrom, allTransfers, stop.stopId]);
    // Memoize uniqueTransfers for transfer details card
    const uniqueTransfers = useMemo(() => {
        const allTransfersArr = [
            ...(stop.transfersTo || []).map(t => ({...t, isTransferTo: true})),
            ...(stop.transfersFrom || []).map(t => ({...t, isTransferFrom: true}))
        ];
        return allTransfersArr.reduce((acc, transfer) => {
            const existing = acc.find(t => t.routeId === transfer.routeId);
            if (existing) {
                // Merge direction flags
                existing.isTransferTo = existing.isTransferTo || transfer.isTransferTo;
                existing.isTransferFrom = existing.isTransferFrom || transfer.isTransferFrom;
                // Merge all timing fields, preferring non-null values
                for (const key of ['departSecs', 'arriveSecs', 'departTime', 'arrivalTime']) {
                    if ((existing[key] === undefined || existing[key] === null) && (transfer[key] !== undefined && transfer[key] !== null)) {
                        existing[key] = transfer[key];
                    }
                }
                return acc;
            } else {
                // Enrich with timing info if missing
                const merged = { ...transfer };
                for (const key of ['departSecs', 'arriveSecs', 'departTime', 'arrivalTime']) {
                    if (merged[key] === undefined || merged[key] === null) {
                        const fullTransfer = getTransferWithTiming(transfer.routeId, stop.stopId, !transfer.isTransferTo);
                        if (fullTransfer && fullTransfer[key] !== undefined && fullTransfer[key] !== null) {
                            merged[key] = fullTransfer[key];
                        }
                    }
                }
                return [...acc, merged];
            }
        }, []);
    }, [stop.transfersTo, stop.transfersFrom, stop.stopId, getTransferWithTiming, allTransfers]);

    // --- NEW: Use allRoutes and allStops for transfer info ---
    const { allRoutes } = useAllRoutes();
    const { allStops: allStopsGlobal } = useAllStops();

    // Helper to get setdown and pickup transfer data (like Transfers.js)
    const getTransferDataForStop = useCallback(() => {
        if (!allRoutes || !allStopsGlobal) return { setdown: [], pickup: [] };
        const stopObj = allStopsGlobal[stop.stopId];
        if (!stopObj) return { setdown: [], pickup: [] };
        // Set linked stops for this stop
        stopObj.setLinkedStops(allStopsGlobal);
        // Get all setdown services (arrivals at this stop)
        const setdownData = [];
        Object.values(allRoutes).forEach(route => {
            (route.services || []).forEach(trip => {
                (trip.stopTimes || []).forEach(st => {
                    if (st.stopId === stop.stopId) {
                        setdownData.push({
                            routeId: route.routeId,
                            tripId: trip.tripId,
                            stopTimeId: st.stopTimeId,
                            arriveSecs: st.arriveSecs,
                            departSecs: st.departSecs,
                            routeNumber: route.routeNumber,
                            trip,
                            st,
                            route
                        });
                    }
                });
            });
        });
        // For each setdown, find possible pickups (connections)
        const pickupData = [];
        setdownData.forEach(setdown => {
            // For each route at this stop (excluding the setdown route)
            Object.values(allRoutes).forEach(route => {
                if (route.routeId === setdown.routeId) return;
                (route.services || []).forEach(trip => {
                    (trip.stopTimes || []).forEach(st => {
                        if (st.stopId === stop.stopId) {
                            // Wait time = pickup departSecs - setdown arriveSecs
                            const dwellSecs = st.departSecs - setdown.arriveSecs;
                            pickupData.push({
                                fromRouteId: setdown.routeId,
                                fromTripId: setdown.tripId,
                                fromStopId: setdown.st.stopId,
                                fromStopTimeId: setdown.st.stopTimeId,
                                toRouteId: route.routeId,
                                toTripId: trip.tripId,
                                toStopId: st.stopId,
                                toStopTimeId: st.stopTimeId,
                                arriveSecs: setdown.arriveSecs,
                                departSecs: st.departSecs,
                                dwellSecs,
                                routeNumber: route.routeNumber,
                                fromRouteNumber: setdown.routeNumber,
                                toRouteNumber: route.routeNumber,
                                trip,
                                st,
                                route
                            });
                        }
                    });
                });
            });
        });
        return { setdown: setdownData, pickup: pickupData };
    }, [allRoutes, allStopsGlobal, stop.stopId]);

    // Memoize event handlers
    const handleSchoolBadgeClick = useCallback((e) => {
        e.stopPropagation();
        setShowSchoolInfo(v => !v);
    }, [setShowSchoolInfo]);
    const handleSchoolBadgeMouseEnter = useCallback((e) => {
        e.target.style.transform = 'translateY(-1px)';
        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    }, []);
    const handleSchoolBadgeMouseLeave = useCallback((e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = 'none';
    }, []);
    const handleTransferBadgeClick = useCallback((e) => {
        e.stopPropagation();
        setShowTransfers(v => !v);
    }, [setShowTransfers]);
    const handleTransferBadgeMouseEnter = useCallback((e) => {
        e.target.style.transform = 'translateY(-1px)';
        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    }, []);
    const handleTransferBadgeMouseLeave = useCallback((e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = 'none';
    }, []);

    return (<>
        <Row key={`StopTime-Row-${stop.stopTimeId}}`}
            onMouseOver={() => {
                if (!editing) {
                    setFocusStop({ ...stop, ...allStops[stop.stopId] });
                }
            }}
            onClick={() => {
                setSelectedStop({ ...stop, ...allStops[stop.stopId] });
            }}
            ref={selectedStop && stop.stopTimeId === selectedStop.stopTimeId ? scrollRef : undefined}
            className={`sb-row flex-nowrap align-items-center ${stop.timingPoint ? 'TP' : ''} ${selectedStop && stop.stopTimeId === selectedStop.stopTimeId ? 'Selected' : ''} ${stopValidationMessages?.length ? 'alert-danger' : ''}`}>
            {!immutable && <Col xs={1} className="text-center">{idx + 1}</Col>}
            <Col xs={2} className="text-center align-self-start" style={{ paddingTop: '2px' }}>{!immutable ?
                <Pin size={25} type={allStops[stop.stopId]?.stopType} /> :
                <Pin size={25} sequence={trip.getStopSequence(stop, allStops)}
                    type={allStops[stop.stopId]?.stopType} />}</Col>
            <Col xs={charter ? 13 : 11} style={{ lineHeight: '18px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {
                    stopValidationMessages?.length ?
                        <Tooltip
                            title={<div>{stopValidationMessages.map(m => (<p key={'vl_' + m.msg}>{m.msg}</p>))}</div>}
                            trigger="click">
                            <span style={{ cursor: 'pointer' }}>{allStops[stop.stopId]?.stopName}</span>
                        </Tooltip>
                        :
                        <span>{allStops[stop.stopId]?.stopName}</span>
                }

                {/* School and Transfer Badges Side by Side */}
                {((isSchoolTrip && schoolBellValidation?.length > 0) || (transferValidation && (transferValidation.transfersTo.length > 0 || transferValidation.transfersFrom.length > 0))) && (
                    <div style={{
                        marginTop: '5px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        {/* School Badge */}
                        {isSchoolTrip && schoolBellValidation && schoolBellValidation.length > 0 && (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    padding: '2px 6px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#fff2f0' : '#f0f8ff',
                                    border: `1px solid ${schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#ffccc7' : '#bae7ff'}`,
                                    color: schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) ? '#cf1322' : '#0958d9',
                                    transition: 'all 0.2s ease',
                                    minWidth: 'fit-content',
                                    maxWidth: '90%',
                                    flexShrink: 1,
                                    // Equal width when both badges are present
                                    ...(transferValidation && (transferValidation.transfersTo.length > 0 || transferValidation.transfersFrom.length > 0) ? {
                                        flex: '1 1 0',
                                        minWidth: '120px'
                                    } : {})
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSchoolInfo(!showSchoolInfo);
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <Pin size={14} type={'school'} />
                                <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    textAlign: 'center'
                                }}>
                                    {schoolBellValidation.length === 1
                                        ? schoolBellValidation[0].schoolStop.stopName
                                        : `${schoolBellValidation.length} schools`
                                    }
                                </span>
                                {schoolBellValidation.some(v => v.hasBellTimes && !v.isValid) && <Warning style={{ height: 11, width: 11 }} />}
                                {showSchoolInfo ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                            </div>
                        )}
                        {/* Transfer Badge */}
                        {transferValidation && (transferValidation.transfersTo.length > 0 || transferValidation.transfersFrom.length > 0) && (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    padding: '2px 6px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: (transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#fff2f0' : '#f0f8ff',
                                    border: `1px solid ${(transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#ffccc7' : '#bae7ff'}`,
                                    color: (transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) ? '#cf1322' : '#0958d9',
                                    transition: 'all 0.2s ease',
                                    minWidth: 'fit-content',
                                    maxWidth: '90%',
                                    flexShrink: 1,
                                    // Equal width when both badges are present
                                    ...(isSchoolTrip && schoolBellValidation && schoolBellValidation.length > 0 ? {
                                        flex: '1 1 0',
                                        minWidth: '120px'
                                    } : {})
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowTransfers(!showTransfers);
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <Transfers style={{ height: 14, width: 14 }} />
                                <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    textAlign: 'center'
                                }}>
                                    {(() => {
                                        // Calculate deduplicated count using the same data as the expanded view
                                        const allTransfers = [
                                            ...(stop.transfersTo || []),
                                            ...(stop.transfersFrom || [])
                                        ];
                                        const uniqueCount = allTransfers.filter((transfer, index, arr) =>
                                            arr.findIndex(t => t.routeId === transfer.routeId) === index
                                        ).length;
                                        return `${uniqueCount} connection${uniqueCount !== 1 ? 's' : ''}`;
                                    })()}
                                </span>
                                {(transferValidation.transfersTo.some(t => t.invalid) || transferValidation.transfersFrom.some(t => t.invalid)) && <Warning style={{ height: 10, width: 10 }} />}
                                {showTransfers ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                            </div>
                        )}
                    </div>
                )}
            </Col>
            {!immutable && !charter && <Col xs={2} className="text-center"><Form.Check
                checked={stop.timingPoint}
                type={'checkbox'}
                id={`${idx}-TP-radio`}
                onChange={(e) => {
                    console.log('TimingPoint clicked', !stop.timingPoint)
                    stop.timingPoint = !stop.timingPoint
                    setTrip(trip, true);
                    setUpdated(true);
                }}

            />
            </Col>}
            <Col xs={4} className="text-center InputTimeField">
                {immutable ?
                    <Flex justify={"space-between"}>
                        <div style={{
                            fontStyle: `${stop.timingPoint ? '' : 'italic'}`,
                            fontWeight: `${stop.timingPoint ? 'bold' : ''}`
                        }}>{stop.timingPoint || route.charter || operator?.opts?.timetable?.stopTimes?.showAll ? getDepartureTimeAsDayjs(trip, stop)?.format('HH:mm') || '-:-' : ''}</div>
                        <div>
                            <ChatButton chatContext={{
                                type: 'route',
                                id: route.routeId,
                                routeId: route.routeId,
                                stopTimeId: stop.stopTimeId,
                                subTypeId: `${trip.tripId}_${stop.stopTimeId}`,
                                stopId: stop.stopId,
                                tripId: trip.tripId,
                                route,
                                stopTime: stop,
                                subType: 'st'
                            }} />
                        </div>
                    </Flex>
                    :
                    <StopTimeField validationMessages={arriveValidationMessages} idx={idx} stop={stop}
                        setEditing={setEditing} setUpdated={setUpdated} trip={trip}
                        cumulativeUpdate={cumulativeUpdate} arrive={true} focus={focus}
                        setFocus={setFocus} showSeconds={showSeconds}
                        onChange={(e, value) => {

                            if (!value) {
                                return
                            }
                            value = dayjs(value, showSeconds ? 'HH:mm:ss' : 'HH:mm')
                            if (!value.isValid()) {
                                return
                            }
                            // const departTime = getDepartureTimeAsDayjs(trip, stop)
                            // stop.dwell = departTime.diff(value, 's')
                            const currentSecs = stop.arriveSecs
                            stop.arriveSecs = dayjsToSecsSinceMidnight(value)
                            if (cumulativeUpdate) {
                                const diff = stop.arriveSecs - currentSecs
                                stop.departSecs += diff
                                stopTimes.forEach((st, i) => {
                                    if (i > idx) {
                                        st.arriveSecs += diff
                                        st.departSecs += diff
                                    }
                                })
                            }
                            setTrip(trip);

                            if (e.shiftKey) {
                                console.log("SHIFT KEY")
                            }
                        }}
                        value={getArrivalTimeAsDayjs(trip, stop).format(showSeconds ? 'HH:mm:ss' : 'HH:mm')}
                    />
                }
                {/*<td>{getDepartureTime(route, stop, idx) || '-'}*/}
                {/*<td>{startHour && startMin ? toTime(stop.delta - getPreviousTimingPoint(route, stop).delta + stop.delta + stop.adjustedDelta) : idx > 0 ? toHrsMinsSecs(stop.delta + stop.adjustedDelta - stopTimes[idx - 1].delta + stopTimes[idx - 1].adjustedDelta) + ' later' : '-'}*/}
            </Col>
            {/*<div className="col-sm-2 d-flex justify-content-center pl-0 pr-0">*/}
            {/*    {!immutable && !stop.timingPoint && idx > 0 &&*/}
            {/*        <div className="sb-controls">*/}
            {/*            <Button className="sb-delete-row" icon={<Minus/>} onClick={(e) => decreaseDelta(e, idx)} />*/}
            {/*            <Button className="sb-add-row" icon={<Plus/>} onClick={(e) => increaseDelta(e, idx)} />*/}
            {/*        </div>*/}
            {/*    }*/}
            {/*</div>*/}

            {!immutable && <Col xs={4} className="text-center InputTimeField">
                <StopTimeField validationMessages={departValidationMessages} idx={idx} stop={stop}
                    setEditing={setEditing} setUpdated={setUpdated} trip={trip} focus={focus}
                    setFocus={setFocus} arrive={false} cumulativeUpdate={cumulativeUpdate}
                    showSeconds={showSeconds}
                    onChange={(e, value) => {

                        if (!value) {
                            return
                        }
                        value = dayjs(value, showSeconds ? 'HH:mm:ss' : 'HH:mm')
                        if (!value.isValid()) {
                            return
                        }
                        // const arrivalTime = getArrivalTimeAsDayjs(trip, stop)
                        // stop.departHour = value ? value.hour() : null;
                        // stop.departMin = value ? value.minute() : null
                        // stop.departSec = value ? value.second() : 0
                        // stop.delta = calculateDelta(trip, stop, idx)
                        // stop.dwell = value.diff(arrivalTime, 's')
                        const currentSecs = stop.departSecs
                        stop.departSecs = dayjsToSecsSinceMidnight(value)
                        if (cumulativeUpdate) {
                            const diff = stop.departSecs - currentSecs
                            stopTimes.forEach((st, i) => {
                                if (i > idx) {
                                    st.arriveSecs += diff
                                    st.departSecs += diff
                                }
                            })
                        }
                        setTrip(trip)
                    }}
                    value={getDepartureTimeAsDayjs(trip, stop).format(showSeconds ? 'HH:mm:ss' : 'HH:mm')}
                />
            </Col>}
            {/*}*/}
            {/*<td>{idx > 0 ? toKmMs(stop.distance) : "-"}</td>*/}
            {/*<td>{idx > 0 ? toHrsMinsSecs(stop.delta + stop.adjustedDelta - (stopTimes[idx - 1].delta + stopTimes[idx - 1].adjustedDelta)) : "-"}</td>*/}
        </Row>

        {/* Show transfer details only when clicked */}
        {showTransfers && (
            (() => {
                // Use allTransfers and modal logic to get only the 'checked' pickup transfers
                const allTransfersArr = Object.values(allTransfers || {});
                // Setdown context for this stopTime - needs to match the structure expected by isSetdownTransfer
                const setdownService = {
                    fromRouteId: route.routeId,
                    fromTripId: trip.tripId,
                    fromStopId: stop.stopId,
                    fromStopTimeId: stop.stopTimeId
                };
                // Show all pickup transfers that match this setdown service (including those with validation errors)
                const selectedPickupTransfers = allTransfersArr.filter(tx =>
                    isSetdownTransfer(tx, setdownService)
                );
                
                // Also show transfers from transferValidation (which includes validation info)
                const validationTransfers = [
                    ...(transferValidation?.transfersTo || []).map(v => ({...v.transfer, isTransferTo: true, validation: v})),
                    ...(transferValidation?.transfersFrom || []).map(v => ({...v.transfer, isTransferFrom: true, validation: v}))
                ];
                
                // Build a set of unique keys for selectedPickupTransfers using toRouteId, toTripId, toStopTimeId
                const selectedPickupKeys = new Set(
                  selectedPickupTransfers.map(tx => `${tx.toRouteId}_${tx.toTripId}_${tx.toStopTimeId}`)
                );

                // For validationTransfers, only include those not in selectedPickupTransfers, and mark them as invalid
                const filteredValidationTransfers = validationTransfers.filter(tx => {
                  const key = `${tx.routeId}_${tx.validation.tripId}_${tx.validation.stopTimeId}`;
                  return !selectedPickupKeys.has(key);
                }).map(tx => ({ ...tx, invalid: true }));

                // Combine both sets: all selectedPickupTransfers (valid), and only unmatched validationTransfers (invalid)
                const allTransfersToShow = [...selectedPickupTransfers, ...filteredValidationTransfers];


                const getTransferKey = (t) => {
                    if (t.toRouteId || t.fromRouteId) {
                        if (t.isTransferFrom) return `from_${t.fromRouteId}`;
                        return `to_${t.toRouteId}`;
                    }
                    if (t.isTransferFrom) return `from_${t.routeId}`;
                    return `to_${t.routeId}`;
                };

                const uniqueTransfersToShow = allTransfersToShow.filter((transfer, index, arr) =>
                    arr.findIndex(t => getTransferKey(t) === getTransferKey(transfer)) === index
                );
                
                if (!uniqueTransfersToShow.length) return null;
                return (
                    <div className="transfer-details-card" style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #E9E5FE',
                        padding: '8px 10px',
                        margin: '0',
                        width: '100%'
                    }}>
                        <Row>
                            <Col xs={3}></Col>
                            <Col xs={21}>
                                <div className="transfer-section">
                                    <div className="transfer-header" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#495057'
                                    }}>
                                        <EyeOutlined style={{ marginRight: '6px' }} />
                                        <span>Connections ({uniqueTransfersToShow.length})</span>
                                    </div>
                                    <div className="transfer-routes" style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                        marginBottom: '12px'
                                    }}>
                                        {console.log('>>> uniqueTransfersToShow', uniqueTransfersToShow)}
                                        {uniqueTransfersToShow.map((tx, idx) => {
                                            const fullTransfer = getTransferWithTiming(tx.routeId, stop.stopId, tx.isTransferFrom, route.routeId);
                                            // Find the full route object for the pickup service
                                            const pickupRoute = allRoutes?.[tx.toRouteId];
                                            const timeDiff = fullTransfer ? fullTransfer.departSecs - fullTransfer.arriveSecs : tx.departSecs - tx.arriveSecs;
                                            const requiresWalking = fullTransfer ? fullTransfer.fromStopId && fullTransfer.toStopId && fullTransfer.fromStopId !== fullTransfer.toStopId 
                                            : tx.fromStopId && tx.toStopId && tx.fromStopId !== tx.toStopId;
                                            const prefix = timeDiff > 0 ? '+' : (timeDiff < 0 ? '-' : '+');
                                            const timeDisplay = timeDiff === 0 ? '0' : toHrsMinsSecs(Math.abs(timeDiff));
                                            // Direction logic
                                            const isSetdown = fullTransfer ? fullTransfer.fromRouteId === route.routeId && fullTransfer.fromTripId === trip.tripId && fullTransfer.fromStopTimeId === stop.stopTimeId 
                                                : tx.fromRouteId === route.routeId && tx.fromTripId === trip.tripId && tx.fromStopTimeId === stop.stopTimeId;
                                            const isPickup = fullTransfer ? fullTransfer.toRouteId === route.routeId && fullTransfer.toTripId === trip.tripId && fullTransfer.toStopTimeId === stop.stopTimeId 
                                                : tx.toRouteId === route.routeId && tx.toTripId === trip.tripId && tx.toStopTimeId === stop.stopTimeId;
                                            let arrow = '';
                                            let arrowColor = '';
                                            if (isSetdown && isPickup) {
                                                arrow = '↔';
                                                arrowColor = '#722ed1';
                                            } else if (isSetdown) {
                                                arrow = '→';
                                                arrowColor = '#1890ff';
                                            } else if (isPickup) {
                                                arrow = '←';
                                                arrowColor = '#52c41a';
                                            }
                                            return (
                                                <div
                                                    key={'tx_pickup_' + tx.toRouteId + '_' + tx.toTripId + '_' + tx.toStopTimeId}
                                                    className={`transfer-single ${(tx.validation?.invalid) ? "transfer-warning" : ""}`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                                        {/* Direction indicator */}
                                                        {arrow && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '-6px',
                                                                left: '-6px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: '#f0f8ff',
                                                                border: `1px solid ${arrowColor}`,
                                                                zIndex: 15
                                                            }} title={isSetdown && isPickup ? 'Both directions' : isSetdown ? 'Setdown (outbound)' : 'Pickup (inbound)'}>
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: '600',
                                                                    color: arrowColor
                                                                }}>{arrow}</span>
                                                            </div>
                                                        )}
                                                        {/* Timing badge */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '-8px',
                                                            left: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '2px',
                                                            zIndex: 10
                                                        }}>
                                                            {requiresWalking && (
                                                                <Walking
                                                                    style={{
                                                                        width: '10px',
                                                                        height: '10px',
                                                                        fill: '#6c757d'
                                                                    }}
                                                                />
                                                            )}
                                                            <span style={{
                                                                fontSize: '10px',
                                                                color: (timeDiff >= 0) ? '#007bff' : '#F5222D',
                                                                fontWeight: '600',
                                                                backgroundColor: '#f8f9fa',
                                                                padding: '0 2px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #e9ecef'
                                                            }}>
                                                                {prefix}{timeDisplay}
                                                            </span>
                                                        </div>
                                                        <RouteNumber
                                                            key={'TxRid' + tx.toRouteId}
                                                            route={pickupRoute || tx}
                                                            link={true}
                                                            meta={true}
                                                            tooltip={tx.validation?.warnings?.length ? uniq(tx.validation.warnings).map(w => <p key={w}>{w}</p>) : (tx.invalid && tx.warnings?.length ? uniq(tx.warnings).map(w => <p key={w}>{w}</p>) : null)}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            })()
        )}

        {/* Show school information details only when clicked */}
        {isSchoolTrip && showSchoolInfo && schoolBellValidation && schoolBellValidation.length > 0 &&
            <div className="school-details-card" style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #E9E5FE',
                padding: '8px 10px',
                margin: '0',
                width: '100%',
            }}>
                <Row>
                    <Col xs={3}></Col>
                    <Col xs={21}>
                        {schoolBellValidation.map((validation, index) => {
                            const hasWarning = validation.hasBellTimes && !validation.isValid;
                            return (
                                <div key={`school-${index}`} style={{ marginBottom: index < schoolBellValidation.length - 1 ? '8px' : '0' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {validation.hasBellTimes ? (
                                            <>
                                                {/* Determine if it's AM or PM based on arrival time */}
                                                {(() => {
                                                    const arrivalHour = parseInt(validation.arrivalTime.split(':')[0]);
                                                    const isAM = arrivalHour < 12;
                                                    return <BellTimeline validation={validation} isAM={isAM} />;
                                                })()}
                                            </>
                                        ) : (
                                            <div style={{
                                                padding: '6px 10px',
                                                backgroundColor: '#e6f7ff',
                                                border: '1px solid #91d5ff',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                color: '#1890ff',
                                                marginTop: '4px'
                                            }}>
                                                <span style={{ fontSize: '13px', fontWeight: '500' }}>No bell times configured</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Col>
                </Row>
            </div>
        }
    </>
    )
}

export default function StopTimes({
    route, charter,
    allStops,
    showSeconds,
    cumulativeUpdate,
    trip,
    setTrip,
    showAllStops,
    editingRoute,
    setFocusStop,
    setSelectedStop,
    selectedStop,
    setUpdated = noop,
    immutable = false,
    setFocusTransfers,
    focusTransfers,
    operator, validationHelper = { isValid: true },
    buildToStop,
    showNewStop, setShowNewStop
}) {
    const { isAdmin } = useAppContext();
    const [editing, setEditing] = useState(false);
    const [executeScroll, scrollRef] = useScroll();
    const [stopTimes, setStopTimes] = useState([]);
    const [focus, setFocus] = useState(null);

    useEffect(() => {
        executeScroll();
    }, [selectedStop, executeScroll]);

    useEffect(() => {
        if (!trip?.stopTimes) return
        // console.log('trip updated... setting stopTimes', trip.stopTimes)
        setStopTimes(immutable ? trip.getVisiblePublicStopTimes(allStops) : trip.stopTimes)
    }, [trip, allStops, immutable]);

    return allStops ? (
        <div className="StopTimes h-100">
            <Row className="sb-row-heading align-items-center" key={`Stop-Header`}>
                {!immutable && <Col xs={1} className="text-center">#</Col>}
                <Col xs={2} className="text-center"></Col>
                <Col xs={charter ? 13 : 11}>Stop Name</Col>
                {!immutable && !route.charter && !charter && <Col xs={2} className="text-center">TP</Col>}
                <Col xs={4} className="text-center">Arrive</Col>
                <Col xs={4} className="text-center">Depart</Col>
            </Row>
            <div className="service-rows-wrap">
                {
                    stopTimes && stopTimes.filter(st => !!allStops[st.stopId]).map((stop, idx) => {
                        const stopValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && (!s.tripId || (s.stop && s.tripId === trip.tripId))) : []
                        const arriveValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && s.tripId === trip.tripId && s.arrive) : []
                        const departValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && s.tripId === trip.tripId && s.depart) : []
                        // if (messages.length) {
                        //     console.log(messages)
                        //     return (
                        //         // <Tooltip title={<div>{messages.map(m => <p>{m}</p>)}</div>}>
                        //         <StopTime idx={idx} isAdmin={isAdmin} immutable={immutable} setEditing={setEditing}
                        //                   editing={editing} validationHelper={validationHelper}
                        //                   setFocusStop={setFocusStop} setSelectedStop={setSelectedStop}
                        //                   selectedStop={selectedStop}
                        //                   scrollRef={scrollRef} setTrip={setTrip} setUpdated={setUpdated} stop={stop}
                        //                   trip={trip}
                        //                   allStops={allStops} operator={operator} stopTimes={stopTimes}/>
                        //     )
                        // }
                        return (
                            <StopTime
                                route={route} charter={charter}
                                key={'st-' + idx} idx={idx} showSeconds={showSeconds} isAdmin={isAdmin}
                                immutable={immutable} focus={focus} setFocus={setFocus}
                                setEditing={setEditing}
                                editing={editing} stopValidationMessages={stopValidationMessages}
                                arriveValidationMessages={arriveValidationMessages}
                                departValidationMessages={departValidationMessages}
                                setFocusStop={setFocusStop} setSelectedStop={setSelectedStop}
                                selectedStop={selectedStop}
                                scrollRef={scrollRef} setTrip={setTrip} setUpdated={setUpdated} stop={stop}
                                trip={trip} cumulativeUpdate={cumulativeUpdate}
                                allStops={allStops} operator={operator} stopTimes={stopTimes}
                                setFocusTransfers={setFocusTransfers} focusTransfers={focusTransfers} />
                        )
                    }
                    )
                }
                {showNewStop && showAllStops && editingRoute &&
                    <NewStopTime allStops={allStops} showNewStop={showNewStop} setShowNewStop={setShowNewStop}
                        buildToStop={buildToStop} setSelectedStop={setSelectedStop} charter={charter} />
                }
            </div>
        </div>
    ) : <LoadMessage message={"Loading stops..."} />;
}
